import React, { FC } from "react";
import { Box, Typography } from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { Empty, Pagination, Spin } from "antd";
import { Container } from "../../components/container/container";
import { Input } from "../../components/input/input";
import {
  MunicipalityItem,
  MunicipalityListMeta,
} from "../../apis/municipalities";
import { MunicipalityIcon, ChevronRightIcon } from "./MunicipalityIcons";
import "./municipality-list.css";

export interface MunicipalityListProps {
  municipalities: MunicipalityItem[];
  meta?: MunicipalityListMeta;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearchEnter?: () => void;
  onPageChange: (page: number) => void;
  onMunicipalityClick: (item: MunicipalityItem) => void;
  loading?: boolean;
}

export const MunicipalityList: FC<MunicipalityListProps> = ({
  municipalities,
  meta,
  searchValue,
  onSearchChange,
  onSearchEnter,
  onPageChange,
  onMunicipalityClick,
  loading,
}) => {
  const hasItems = municipalities?.length > 0;

  return (
    <div
      className="xvia-municipality-list"
      id="municipality-list"
      data-testid="municipality-list"
    >
      <Container className="xvia-municipality-list__container">
        <Box className="xvia-municipality-list__header">
          <Typography
            variant="h4"
            className="xvia-municipality-list__title-text"
            id="municipality-list-title"
            data-testid="municipality-list-title"
          >
            Municípios
          </Typography>
          <div className="xvia-municipality-list__search">
            <Input
              id="municipality-search-input"
              title=""
              placeholder="Buscar Município"
              className="xvia-municipality-list__search-input"
              value={searchValue}
              onTextChange={onSearchChange}
              onEnterPress={onSearchEnter}
              data-testid="municipality-search-input"
            />
            <span className="xvia-municipality-list__search-icon">
              <SearchOutlinedIcon />
            </span>
          </div>
        </Box>

        <div
          className="xvia-municipality-list__items"
          id="municipality-list-items"
        >
          {loading && (
            <div
              className="xvia-municipality-list__loading"
              id="municipality-list-loading"
              data-testid="municipality-list-loading"
            >
              <Spin size="large" />
            </div>
          )}

          {!loading &&
            municipalities?.map((item) => {
              const totalServices = item.statistics?.total_services ?? 0;
              const totalCategories = item.statistics?.total_categories ?? 0;
              const hasServices = totalServices > 0 || totalCategories > 0;
              const hasMunicipalityLink = Boolean(
                item.parameters?.municipality_link
              );
              const isNonAdherent = !hasServices && !hasMunicipalityLink;
              const shouldShowStatsDescription = hasServices;
              const shouldShowNonAdherentDescription = isNonAdherent;
              let cardAriaLabel = `Acessar portal da prefeitura de ${item.municipality}`;
              if (isNonAdherent) {
                cardAriaLabel = `${item.municipality} não aderente ao Pactos pelo Piauí`;
              } else if (hasServices) {
                cardAriaLabel = `Acessar serviços de ${item.municipality}`;
              }

              return (
                <button
                  key={item.id}
                  id={`municipality-item-${item.id}`}
                  type="button"
                  className={[
                    "xvia-municipality-list__item",
                    isNonAdherent
                      ? "xvia-municipality-list__item--disabled"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => onMunicipalityClick(item)}
                  disabled={isNonAdherent}
                  data-testid={`municipality-item-${item.id}`}
                  aria-label={cardAriaLabel}
                >
                  <div className="xvia-municipality-list__item-left">
                    <div className="xvia-municipality-list__item-icon">
                      <MunicipalityIcon className="xvia-municipality-list__icon" />
                    </div>
                    <div className="xvia-municipality-list__item-text">
                      <Typography className="xvia-municipality-list__item-title">
                        {item.municipality}
                      </Typography>
                      {(shouldShowStatsDescription ||
                        shouldShowNonAdherentDescription) && (
                        <Typography className="xvia-municipality-list__item-subtitle">
                          {shouldShowNonAdherentDescription
                            ? "Município não aderente ao Pactos pelo Piauí"
                            : `${totalServices} Serviços em ${totalCategories} Categorias`}
                        </Typography>
                      )}
                    </div>
                  </div>
                  <div className="xvia-municipality-list__item-right">
                    <ChevronRightIcon className="xvia-municipality-list__chevron" />
                  </div>
                </button>
              );
            })}

          {!loading && !hasItems && (
            <div id="municipality-list-empty">
              <Empty
                style={{ marginTop: "2rem" }}
                description="Nenhum município encontrado"
              />
            </div>
          )}
        </div>

        {meta && meta.total_records > 0 && (
          <div
            className="xvia-municipality-list__pagination"
            id="municipality-pagination"
            data-testid="municipality-pagination"
          >
            <Pagination
              defaultCurrent={1}
              current={meta.current_page}
              total={meta.total_records}
              pageSize={meta.per_page}
              onChange={onPageChange}
              showSizeChanger={false}
            />
          </div>
        )}
      </Container>
    </div>
  );
};
